import { useState } from "react";
import {
  ShieldCheck,
  CheckCircle,
  Star,
  Check,
  ArrowRight,
  Search,
  Phone,
  ClipboardCheck,
  Ban,
  FileCheck,
  Unlock,
  Home,
  Microscope,
  ListChecks,
  Wrench,
  BadgeCheck,
  Lightbulb,
  Ruler,
  Layers,
  Settings,
  Target,
  ChevronDown,
  SearchCheck,
  MapPin,
  FileText,
  Award,
} from "lucide-react";
import { EnquiryForm } from "@/components/enquiry-form";
import { ServiceHeroFloatLayer } from "@/components/service-hero-float-layer";
import { AccreditationStrip } from "@/components/site-blocks";
import { TESTIMONIALS } from "@/lib/site";
import { ReviewProvenanceNote, ReviewSourceBadge } from "@/components/review-source";
import textureBlob from "@/assets/damp-survey/texture-teal-blob.webp";
import heroMobile from "@/assets/damp-survey/hero-house-mobile-828.webp";
import hero1024 from "@/assets/damp-survey/hero-house-1024.webp";
import hero1600 from "@/assets/damp-survey/hero-house-1600.webp";
import {
  DAMP_SURVEY_HERO,
  DAMP_SURVEY_HERO_FLOAT_IMAGES,
  DAMP_SURVEY_HELP_STRIP,
  DAMP_SURVEY_SAMPLE_REPORT_INTRO,
  DAMP_TYPE_CARDS,
  DAMP_SURVEY_PROCESS,
  DAMP_SURVEY_PRICING,
  DAMP_SURVEY_FAQS,
  DAMP_SURVEY_CONTACT,
} from "@/content/damp-survey";
import { DampSurveyHeader } from "@/components/damp-survey/header";
import { DampSurveyFooter } from "@/components/damp-survey/footer";
import { SampleReportViewer } from "@/components/damp-survey/sample-report-viewer";
import { DiagnosisRemediationSection } from "@/components/damp-survey/diagnosis-remediation-section";

const SOURCE_PAGE = "/damp-survey";

function scrollToHeroForm() {
  if (typeof window === "undefined") return;
  document.getElementById("damp-survey-hero-form")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function scrollToSampleReport() {
  if (typeof window === "undefined") return;
  document.getElementById("sample-report")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function scrollToMethodology() {
  if (typeof window === "undefined") return;
  document.getElementById("methodology")?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

export function DampSurveyPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <DampSurveyHeader />
      <main className="flex-1">
        <Hero />
        <HelpStrip />
        <AccreditationsSection />
        <DampTypesSection />
        <SurveyProcessSection />
        <DiagnosisRemediationSection />
        <PricingSection />
        <FaqSection />
        <SampleReportSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <DampSurveyFooter />
    </div>
  );
}

/* ──────────────── Hero ──────────────── */
function Hero() {
  const trust = [ShieldCheck, CheckCircle, Star];
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <picture>
          <source media="(max-width: 1023px)" srcSet={heroMobile} />
          <source media="(max-width: 1399px)" srcSet={hero1024} />
          <img
            src={hero1600}
            alt="Surveyor inspecting damp on the exterior of a residential property"
            className="w-full h-full object-cover"
            width={1600}
            height={1000}
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/55 to-transparent max-md:bg-gradient-to-b max-md:from-primary/85 max-md:via-primary/55 max-md:to-primary/20" />
        <div className="absolute inset-x-0 bottom-0 h-24 md:h-32 bg-gradient-to-b from-transparent to-background pointer-events-none" />
      </div>

      <ServiceHeroFloatLayer images={DAMP_SURVEY_HERO_FLOAT_IMAGES} />

      <div className="relative z-[3] container-page py-10 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-center">
          <div className="max-w-2xl">
            <h1 className="text-3xl md:text-6xl lg:text-7xl font-extrabold text-primary-foreground leading-[1.05] mb-3 md:mb-6 tracking-tight">
              {DAMP_SURVEY_HERO.h1}
            </h1>
            <p className="text-sm md:text-lg text-primary-foreground/85 mb-4 md:mb-6 leading-relaxed">
              {DAMP_SURVEY_HERO.subtitle}
            </p>

            <div className="hidden md:block mt-4 md:mt-6 mb-6 md:mb-8 max-w-xl rounded-2xl bg-foreground/35 backdrop-blur-md border border-primary-foreground/15 p-5 md:p-6 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-foreground/85 mb-2">
                {DAMP_SURVEY_HERO.sampleCardEyebrow}
              </p>
              <p className="text-sm text-primary-foreground mb-4 leading-relaxed">
                {DAMP_SURVEY_HERO.sampleCardBody}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={scrollToSampleReport}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-foreground/95 text-foreground text-sm font-bold hover:bg-primary-foreground transition-colors shadow-lg ring-1 ring-primary-foreground/40"
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  {DAMP_SURVEY_HERO.sampleCardCta}
                </button>
                <button
                  type="button"
                  onClick={scrollToMethodology}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-background/20 backdrop-blur-sm hover:bg-background/30 text-primary-foreground text-sm font-bold border border-primary-foreground/20 transition-colors shadow-lg"
                >
                  <Wrench className="h-4 w-4 shrink-0" />
                  View Solutions
                </button>
              </div>
            </div>

            <div className="hidden md:flex flex-wrap gap-2">
              {DAMP_SURVEY_HERO.badges.map((label, i) => {
                const Icon = trust[i] ?? ShieldCheck;
                return (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground/90 backdrop-blur-sm"
                  >
                    <Icon className="h-3.5 w-3.5 opacity-80" />
                    {label}
                  </span>
                );
              })}
            </div>
          </div>

          <div id="damp-survey-hero-form" className="scroll-mt-24">
            <EnquiryForm
              formType="survey"
              variant="survey"
              defaultService="Damp Survey"
              sourcePage={SOURCE_PAGE}
              title="Get In Touch"
              subtitle="Tell us what you're seeing and we'll be in touch shortly with the right next step."
              submitLabel="Request My Damp Survey"
            />

            {/* Mobile only: Sample report CTA below the form */}
            <div className="md:hidden mt-5 max-w-xl rounded-2xl bg-foreground/40 backdrop-blur-md border border-primary-foreground/15 p-4 shadow-lg">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary-foreground/85 mb-2">
                {DAMP_SURVEY_HERO.sampleCardEyebrow}
              </p>
              <p className="text-sm text-primary-foreground mb-4 leading-relaxed">
                {DAMP_SURVEY_HERO.sampleCardBody}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={scrollToSampleReport}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-primary-foreground/95 text-foreground text-xs font-bold hover:bg-primary-foreground transition-colors shadow-lg ring-1 ring-primary-foreground/40"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  {DAMP_SURVEY_HERO.sampleCardCta}
                </button>
                <button
                  type="button"
                  onClick={scrollToMethodology}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-background/20 backdrop-blur-sm hover:bg-background/30 text-primary-foreground text-xs font-bold border border-primary-foreground/20 transition-colors shadow-lg"
                >
                  <Wrench className="h-3.5 w-3.5 shrink-0" />
                  View Solutions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Sample Report ──────────────── */
function SampleReportSection() {
  return (
    <section id="sample-report" className="scroll-mt-20 py-12 md:py-20 bg-secondary/40">
      <div className="container-page max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent mb-2">
            {DAMP_SURVEY_SAMPLE_REPORT_INTRO.eyebrow}
          </p>
          <h2 className="text-2xl md:text-4xl font-extrabold text-foreground mb-3 leading-tight">
            {DAMP_SURVEY_SAMPLE_REPORT_INTRO.heading}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {DAMP_SURVEY_SAMPLE_REPORT_INTRO.intro}
          </p>
        </div>
        <SampleReportViewer />
      </div>
    </section>
  );
}

/* ──────────────── Help strip ──────────────── */
function HelpStrip() {
  const icons = [SearchCheck, ClipboardCheck, ShieldCheck];
  const chipIcons = [BadgeCheck, ShieldCheck, Wrench, ClipboardCheck];
  return (
    <section className="bg-gradient-to-b from-background to-card">
      <div className="container-page pt-10 md:pt-14 pb-10 md:pb-14">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2">
            {DAMP_SURVEY_HELP_STRIP.title}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {DAMP_SURVEY_HELP_STRIP.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {DAMP_SURVEY_HELP_STRIP.cards.map((card, i) => {
            const Icon = icons[i] ?? ClipboardCheck;
            return (
              <div
                key={card.title}
                className="bg-card rounded-2xl border border-border p-5 md:p-6 shadow-soft hover:shadow-lift transition-shadow"
              >
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-bold text-foreground text-base md:text-lg mb-1.5 tracking-tight">
                  {card.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {card.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap justify-center gap-2 mt-8">
          {DAMP_SURVEY_HELP_STRIP.chips.map((label, i) => {
            const Icon = chipIcons[i] ?? BadgeCheck;
            return (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/5 border border-primary/15 text-primary/80"
              >
                <Icon className="h-3 w-3" />
                {label}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Accreditations ──────────────── */
function AccreditationsSection() {
  return (
    <section className="bg-card border-y border-border py-10">
      <p className="text-xs text-muted-foreground text-center mb-6 font-semibold uppercase tracking-wider">
        Fully Accredited, Fully Insured
      </p>
      <AccreditationStrip />
    </section>
  );
}

/* ──────────────── Damp types ──────────────── */
function DampTypesSection() {
  return (
    <section className="py-16 md:py-24">
      <div className="container-page">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <h2 className="text-2xl md:text-4xl font-extrabold text-foreground leading-tight">
            Not Sure What Type Of Damp You Have?
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Damp, mould and moisture problems can look similar, but the causes are often different. These guides help you recognise common signs. The right solution depends on the property, the symptoms and where the moisture is coming from.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DAMP_TYPE_CARDS.map((card) => (
            <article
              key={card.title}
              className="group flex flex-col h-full bg-card rounded-2xl border border-border shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-secondary">
                <img
                  src={card.image}
                  alt={card.imageAlt}
                  loading="lazy"
                  width={1280}
                  height={720}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col flex-1 p-5 md:p-6 gap-4">
                <h3 className="text-lg md:text-xl font-bold text-foreground">{card.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.explanation}</p>

                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-primary mb-2">
                    Typical Signs
                  </p>
                  <ul className="space-y-1.5">
                    {card.signs.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-foreground/90">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
                        <span className="leading-snug">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-xl bg-primary/[0.06] border border-primary/15 p-3.5">
                  <p className="flex items-start gap-2 text-xs text-foreground/85 leading-relaxed">
                    <Search className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
                    <span>
                      <span className="font-semibold text-foreground">How We Check It: </span>
                      {card.howWeCheck}
                    </span>
                  </p>
                </div>

                <div className="mt-auto pt-2">
                  <button
                    type="button"
                    onClick={scrollToHeroForm}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                  >
                    Check My Home
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Survey process ──────────────── */
function SurveyProcessSection() {
  const { title, intro, steps, trustHeadline, trustBody, trustPillars, reportHeadline, reportBody, reportSections, bottomCtaHeadline, bottomCtaBody } = DAMP_SURVEY_PROCESS;
  const stepIcons = [Phone, Search, ShieldCheck];
  const pillarIcons = [Ban, FileCheck, Unlock];
  const reportIcons = [Home, Microscope, ClipboardCheck, ListChecks, Wrench, BadgeCheck];

  return (
    <section className="bg-primary/5 py-16 md:py-24">
      <div className="container-page">
        <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">{intro}</p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="hidden lg:block absolute top-10 left-[12%] right-[12%] h-px bg-primary/20" aria-hidden />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 relative">
            {steps.map((s, i) => {
              const Icon = stepIcons[i] ?? ShieldCheck;
              return (
                <div
                  key={s.n}
                  className="relative bg-card border border-border rounded-2xl p-6 md:p-7 shadow-soft hover:shadow-lift transition-shadow flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="relative w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md z-10">
                      <Icon className="h-6 w-6" />
                      <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-card border-2 border-primary text-primary text-xs font-extrabold flex items-center justify-center">
                        {s.n}
                      </span>
                    </div>
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-primary">
                      Step {s.n}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 leading-snug">
                    {s.headline}
                  </h3>
                  <p className="text-sm md:text-[15px] text-muted-foreground leading-relaxed mb-5">
                    {s.body}
                  </p>
                  <div className="mt-auto pt-4 border-t border-border/60">
                    <p className="text-[11px] font-extrabold uppercase tracking-widest text-foreground/70 mb-1.5">
                      You'll Get
                    </p>
                    <div className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <p className="text-sm text-foreground leading-relaxed">{s.outcome}</p>
                    </div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="hidden lg:flex absolute top-10 -right-5 z-20 w-10 h-10 rounded-full bg-card border border-primary/30 items-center justify-center shadow-sm">
                      <ArrowRight className="h-4 w-4 text-primary" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="max-w-5xl mx-auto bg-primary/5 rounded-2xl p-8 md:p-12 mt-12 mb-12">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-4 leading-snug">
                {trustHeadline}
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">{trustBody}</p>
              <div className="space-y-5">
                {trustPillars.map((pillar, i) => {
                  const Icon = pillarIcons[i] ?? Ban;
                  return (
                    <div key={pillar.title} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">{pillar.title}</p>
                        <p className="text-sm text-muted-foreground">{pillar.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-lg md:text-xl font-bold text-foreground mb-2">{reportHeadline}</h4>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">{reportBody}</p>
              <div className="space-y-3">
                {reportSections.map((section, i) => {
                  const Icon = reportIcons[i] ?? BadgeCheck;
                  const isHighlighted = section === "What Is NOT Required";
                  return (
                    <div
                      key={section}
                      className={`flex items-center gap-3 rounded-lg px-4 py-3 ${
                        isHighlighted
                          ? "bg-accent-soft border border-accent/30"
                          : "bg-card border border-border"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isHighlighted ? "bg-accent/15" : "bg-primary/10"
                        }`}
                      >
                        <Icon className={`h-4 w-4 ${isHighlighted ? "text-accent" : "text-primary"}`} />
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          isHighlighted ? "text-foreground font-bold" : "text-foreground"
                        }`}
                      >
                        {section}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  Want to see what a finished report looks like? Browse our fictional sample with every section laid out the same way as yours will be.
                </p>
                <button
                  type="button"
                  onClick={scrollToSampleReport}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-primary text-primary text-sm font-bold hover:bg-primary/10 transition-colors"
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  View Sample Report
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto bg-card border border-border rounded-2xl p-6 md:p-10 text-center shadow-soft">
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 leading-snug">
            {bottomCtaHeadline}
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
            {bottomCtaBody}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={scrollToHeroForm}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
            >
              Start My Damp Survey
              <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="tel:+448000092992"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary px-6 py-3.5 font-semibold hover:bg-primary/10 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Call 0800 009 2992
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Pricing ──────────────── */
function PricingSection() {
  const c = DAMP_SURVEY_PRICING;
  const factorIcons = [Ruler, Home, Layers, Search, Settings, Target];

  return (
    <section id="pricing" className="py-16 md:py-24 bg-card relative overflow-hidden">
      <img
        src={textureBlob}
        alt=""
        aria-hidden
        className="absolute top-0 right-0 w-80 opacity-[0.07] pointer-events-none select-none -rotate-12"
      />
      <div className="container-page relative z-10">
        <div className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{c.heading}</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">{c.intro}</p>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-5 mb-10 max-w-3xl">
          <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-foreground leading-relaxed font-semibold">
            {c.deductionBanner}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          <PricingCard data={c.essential} badgeLabel={null} primary={false} />
          <PricingCard
            data={c.full}
            badgeLabel={c.full.badgeLabel}
            includesPrefix={c.full.includesPrefix}
            primary
          />
        </div>

        <div className="rounded-2xl border border-border bg-background p-8 mb-2">
          <h4 className="font-bold text-foreground mb-5">{c.factorsHeading}</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {c.factors.map((item, i) => {
              const Icon = factorIcons[i] ?? Target;
              return (
                <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border/60">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
            {c.factorsFootnote}
          </p>
        </div>
      </div>
    </section>
  );
}

function PricingCard({
  data,
  badgeLabel,
  includesPrefix,
  primary,
}: {
  data: {
    title: string;
    price: string;
    subtitle: string;
    bullets: readonly string[];
    badge: string;
    cta: string;
  };
  badgeLabel: string | null;
  includesPrefix?: string;
  primary: boolean;
}) {
  return (
    <div
      className={`bg-background rounded-2xl shadow-soft p-8 flex flex-col relative transition-transform duration-200 hover:-translate-y-1 ${
        primary ? "border-2 border-primary" : "border border-border"
      }`}
    >
      {badgeLabel && (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 text-xs font-bold text-primary-foreground bg-primary rounded-full px-3 py-1">
          <Award className="h-3 w-3" /> {badgeLabel}
        </span>
      )}
      <h3 className="text-lg font-bold text-foreground mb-2">{data.title}</h3>
      <p className="text-4xl font-bold text-primary mb-2">{data.price}</p>
      <p className="text-sm text-muted-foreground mb-6">{data.subtitle}</p>
      <ul className="space-y-3 mb-auto">
        {includesPrefix && (
          <li className="text-sm font-bold text-foreground">{includesPrefix}</li>
        )}
        {data.bullets.map((item) => (
          <li key={item} className="text-sm text-muted-foreground flex items-start gap-3">
            <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            {item}
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-5 border-t border-border">
        <span className="inline-block text-xs font-bold text-foreground bg-primary/15 rounded-full px-3 py-1 mb-4">
          {data.badge}
        </span>
        <button
          type="button"
          onClick={scrollToHeroForm}
          className={`w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-bold transition-colors ${
            primary
              ? "bg-accent text-accent-foreground hover:bg-accent/90 shadow-cta"
              : "border-2 border-primary text-primary hover:bg-primary/10"
          }`}
        >
          {data.cta}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ──────────────── FAQ ──────────────── */
function FaqSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section id="faq" className="py-16 md:py-24">
      <div className="container-page">
        <div className="text-center mb-10">
          <p className="text-primary font-bold text-sm mb-2">
            We Don't Just Patch The Symptoms, We Engineer A Solution
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {DAMP_SURVEY_FAQS.map((faq, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={faq.q}
                className="bg-card border border-border rounded-xl shadow-soft overflow-hidden"
              >
                <button
                  type="button"
                  className="w-full text-left p-5 flex items-center justify-between gap-4"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-sm text-foreground">{faq.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-primary transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── Testimonials ──────────────── */
function TestimonialsSection() {
  return (
    <section className="py-16 md:py-24 bg-secondary/50">
      <div className="container-page">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Loved By Homeowners, Trusted By Experts
          </h2>
          <p className="text-muted-foreground">
            Real feedback from CRS customers across Wales and the South West.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="bg-card border border-border rounded-2xl shadow-soft p-7 flex flex-col relative"
            >
              <span className="absolute top-4 right-6 text-5xl text-primary/10 font-serif leading-none select-none">"</span>
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                "{t.quote}"
              </p>
              <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-primary">
                    {t.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-foreground text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.date}</p>
                </div>
              </div>
              <ReviewSourceBadge source={t.source} className="mt-4" />
            </div>
          ))}
        </div>
        <ReviewProvenanceNote className="mt-8" />
      </div>

    </section>
  );
}

/* ──────────────── Contact ──────────────── */
function ContactSection() {
  const bulletIcons = [Phone, ShieldCheck, MapPin];
  return (
    <section id="contact" className="py-16 md:py-24 relative overflow-hidden">
      <img
        src={textureBlob}
        alt=""
        aria-hidden
        className="absolute bottom-0 right-0 w-96 opacity-[0.06] pointer-events-none select-none rotate-90"
      />
      <div className="container-page relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-accent mb-3">
              {DAMP_SURVEY_CONTACT.eyebrow}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
              {DAMP_SURVEY_CONTACT.heading}
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {DAMP_SURVEY_CONTACT.intro}
            </p>
            <ul className="space-y-3">
              {DAMP_SURVEY_CONTACT.bullets.map((b, i) => {
                const Icon = bulletIcons[i] ?? Phone;
                return (
                  <li key={b} className="flex items-center gap-3 text-sm font-semibold text-foreground">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    {b}
                  </li>
                );
              })}
            </ul>
            <p className="mt-8 text-sm text-muted-foreground">
              Prefer to speak to someone?{" "}
              <a
                href="tel:+448000092992"
                className="font-semibold text-accent hover:underline"
              >
                Call 0800 009 2992
              </a>
            </p>
          </div>

          <EnquiryForm
            formType="contact"
            variant="contact"
            defaultService="Damp Survey"
            sourcePage={`${SOURCE_PAGE}#contact`}
            title="Send Us A Message"
            subtitle="We'll be in touch shortly with the right next step."
            submitLabel="Send My Enquiry"
          />
        </div>
      </div>
    </section>
  );
}
